/**
 * De leerlingen in één les, met per leerling de instellingen die voor hem
 * gelden: die van de groep, of een afwijking (niveau, aantal vragen). Een
 * rij met een afwijking is geel, met de knop om terug te gaan naar de
 * groepsinstellingen. De tekstlengte staat erbij, maar volgt sinds stap 18
 * het niveau; die kies je niet.
 *
 * Het formulier per rij las zijn waarden met document.getElementById uit de
 * pagina; nu houdt het ze zelf bij (het concept), zoals elk ander formulier.
 *
 * Opslaan stuurt alleen wat de leerkracht veranderde. Vroeger gingen alle
 * waarden mee, ook de onveranderde: dan stond de leerling op "Aangepast", en
 * zat hij vast aan bijvoorbeeld zijn niveau van dat moment, ook als dat
 * later werd aangepast. (Er was ook een kolom Chat; die instelling deed
 * niets en is weg.)
 *
 * Een leerling die al aan de les begon, kan niet meer anders worden
 * ingesteld: wat hij leest en zijn toets liggen dan vast, en de server
 * weigert het (409). Zijn rij heeft daarom geen knoppen, maar een slotje.
 */
import React, { useEffect, useState } from 'react';
import { ChevronLeft, Lock, RotateCcw, Save, Settings, X } from 'lucide-react';
import { leerkrachtApi as api } from '../../../api/leerkracht';
import { AANTALLEN_MEERKEUZE, AANTALLEN_OPEN, NIVEAUS } from '../../../keuzelijsten';

const klein = { padding: '4px', fontSize: '13px' };

/** Begonnen: zijn instellingen voor deze les liggen vast. */
const isBegonnen = (ss) => ss.status && ss.status !== 'niet_gestart';

/** Het concept voor één rij, uit wat de server nu voor die leerling geeft. */
const conceptVan = (ss) => ({
  niveau: ss.niveau || '',
  meerkeuze: ss.close_reading_aantal,
  open: ss.evaluatie_aantal,
});

export function LeerlingInstellingen({ les, onTerug, meldFout }) {
  const [leerlingen, setLeerlingen] = useState([]);
  const [bewerkt, setBewerkt] = useState(null);     // het id van de leerling in het formulier
  const [concept, setConcept] = useState(null);

  const laad = async () => {
    try {
      setLeerlingen(await api.haalLeerlingenInLes(les.id));
    } catch (err) {
      meldFout(err.message);
    }
  };

  useEffect(() => {
    laad();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen
  }, [les.id]);

  const begin = (ss) => {
    setBewerkt(ss.student_id);
    setConcept(conceptVan(ss));
  };

  const bewaar = async (ss) => {
    const was = conceptVan(ss);
    const afwijking = {};
    // Een leeg niveau ("Standaard") haalt de afwijking weg; de server krijgt dan ''.
    if (concept.niveau !== was.niveau) afwijking.niveau_override = concept.niveau;
    if (String(concept.meerkeuze) !== String(was.meerkeuze)) afwijking.close_reading_override = parseInt(concept.meerkeuze);
    if (String(concept.open) !== String(was.open)) afwijking.evaluatie_override = parseInt(concept.open);
    try {
      if (Object.keys(afwijking).length > 0) {
        await api.bewaarAfwijking(les.id, ss.student_id, afwijking);
        await laad();
      }
      setBewerkt(null);
    } catch (err) {
      meldFout(err.message);
    }
  };

  const zetTerug = async (leerlingId) => {
    try {
      await api.wisAfwijkingen(les.id, leerlingId);
      await laad();
    } catch (err) {
      meldFout(err.message);
    }
  };

  const zet = (veld) => (e) => setConcept({ ...concept, [veld]: e.target.value });

  return (
    <>
      <div className="flex-between mb-16">
        <div className="flex gap-8" style={{ alignItems: 'center' }}>
          <button className="btn btn-secondary" onClick={onTerug}><ChevronLeft size={16} /> Terug</button>
          <h3 style={{ margin: 0 }}>{les.naam}</h3>
        </div>
        <div className="text-muted">Groep: {les.class_naam} | Tekst: {les.text_titel}</div>
      </div>

      <div className="card mb-16" style={{ background: 'var(--blauw-vlak)', padding: '12px 16px' }}>
        <strong>Groepsinstellingen:</strong>{' '}
        Illustratie: {les.illustratie_stijl || 'Geen'} | lengte volgens het niveau
        {' '}| CR: {les.close_reading_aantal} MC | Evaluatie: {les.evaluatie_aantal} open
      </div>

      <table>
        <thead>
          <tr>
            <th>Leerling</th>
            <th>Niveau</th>
            <th>Tekstlengte</th>
            <th>Close Reading</th>
            <th>Evaluatie</th>
            <th>Status</th>
            <th>Acties</th>
          </tr>
        </thead>
        <tbody>
          {leerlingen.map(ss => (
            <tr key={ss.id} style={{ background: ss.has_overrides ? 'var(--waarschuw-vlak)' : 'var(--wit)' }}>
              <td>
                {ss.student_naam}
                {ss.has_overrides && <span className="badge badge-warning" style={{ marginLeft: 8 }}>Aangepast</span>}
              </td>
              {bewerkt === ss.student_id ? (
                <>
                  <td>
                    <select className="input" style={klein} value={concept.niveau} onChange={zet('niveau')}>
                      <option value="">Standaard</option>
                      {NIVEAUS.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </td>
                  <td className="text-muted" title="De lengte volgt het niveau">{ss.tekstlengte}</td>
                  <td>
                    <select className="input" style={klein} value={concept.meerkeuze} onChange={zet('meerkeuze')}>
                      {AANTALLEN_MEERKEUZE.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </td>
                  <td>
                    <select className="input" style={klein} value={concept.open} onChange={zet('open')}>
                      {AANTALLEN_OPEN.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </td>
                  <td><span className="badge">{ss.status}</span></td>
                  <td>
                    <div className="flex gap-4">
                      <button className="btn btn-success" style={{ padding: '4px 8px' }} onClick={() => bewaar(ss)}>
                        <Save size={14} />
                      </button>
                      <button className="btn btn-secondary" style={{ padding: '4px 8px' }} onClick={() => setBewerkt(null)}>
                        <X size={14} />
                      </button>
                    </div>
                  </td>
                </>
              ) : (
                <>
                  <td>{ss.niveau || '-'}</td>
                  <td>{ss.tekstlengte}</td>
                  <td>{ss.close_reading_aantal}</td>
                  <td>{ss.evaluatie_aantal}</td>
                  <td><span className="badge">{ss.status}</span></td>
                  <td>
                    {isBegonnen(ss) ? (
                      <span className="text-muted text-small" title="Deze leerling is al begonnen; zijn instellingen liggen vast.">
                        <Lock size={14} style={{ verticalAlign: 'middle' }} /> ligt vast
                      </span>
                    ) : (
                      <div className="flex gap-4">
                        <button className="btn btn-primary" style={{ padding: '4px 8px' }} onClick={() => begin(ss)} title="Bewerken">
                          <Settings size={14} />
                        </button>
                        {ss.has_overrides && (
                          <button className="btn btn-warning" style={{ padding: '4px 8px' }} onClick={() => zetTerug(ss.student_id)}
                            title="Reset naar groepsinstellingen">
                            <RotateCcw size={14} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
